// The student plan cards in the upgrade window (Josh's mock v2, approved 15 Oct 2026).
//
// Mirrors functions/lib/student-rate.js, which is what Checkout and the switch actually enforce: the window
// runs from the account's FIRST student start (`student_rate_started_at`, written once by the server) for
// `student_rate_term_years` (whole years 1-12, default 4), and keeps running while unsubscribed. An
// unreadable start counts as never started, as on the server (it fails open).
//
// States: 'new' (never had it: the window would start today), 'returning' (inside the window, not on the
// student price now), 'ended' (window over: refused at Checkout), 'current' (on the student price now).

const DEFAULT_TERM_YEARS = 4;

export function termYears(user) {
    const raw = user && user.student_rate_term_years;
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isInteger(n) && n >= 1 && n <= 12 ? n : DEFAULT_TERM_YEARS;
}

function startOf(user) {
    const v = user && user.student_rate_started_at;
    if (!v) return null;
    let ms = NaN;
    if (typeof v.toDate === 'function') ms = v.toDate().getTime();
    else if (typeof v === 'object' && typeof v.seconds === 'number') ms = v.seconds * 1000;
    else ms = Date.parse(v);
    return Number.isFinite(ms) ? new Date(ms) : null;
}

// Same as the server's addYearsUTC: calendar years in UTC (Feb 29 + 4 years is Feb 29).
function addYearsUTC(date, years) {
    const d = new Date(date.getTime());
    d.setUTCFullYear(d.getUTCFullYear() + years);
    return d;
}

function currentInterval(user, ids) {
    if (!user || !user.active_pay) return null;
    if (ids && ids.year && user.price_id === ids.year) return 'year';
    if (ids && ids.month && user.price_id === ids.month) return 'month';
    if (user.subscription_level === 'student') return 'month';
    return null;
}

export function studentWindow(user, now, ids) {
    const at = now || new Date();
    const years = termYears(user);
    const start = startOf(user);
    const interval = currentInterval(user, ids);
    if (interval) {
        return { state: 'current', interval, years, endsAt: addYearsUTC(start || at, years) };
    }
    if (!start) return { state: 'new', interval: null, years, endsAt: addYearsUTC(at, years) };
    const endsAt = addYearsUTC(start, years);
    return { state: at.getTime() >= endsAt.getTime() ? 'ended' : 'returning', interval: null, years, endsAt };
}

const pad = n => (n < 10 ? `0${n}` : `${n}`);

// MM/DD/YYYY like the rest of the window, in UTC so the day matches the server's date.
export function formatDate(date) {
    return `${pad(date.getUTCMonth() + 1)}/${pad(date.getUTCDate())}/${date.getUTCFullYear()}`;
}

export function endLine(endsAt, interval) {
    const which = interval === 'year' ? 'yearly' : 'monthly';
    return `Your student price will end on ${formatDate(endsAt)}. After that date you will be charged the standard Individual ${which} price.`;
}

// The Account page Subscription card (Josh, 15 Oct 2026: option 1 of the card mock). Null unless the account
// is on a student price right now, is not cancelling, and is not on Dual; the card then shows
// "Your {planName} subscription. {endsLine}". No Student badge (Josh: too much beside Active).
export function accountCardStudent(user, now, ids) {
    if (!ids || !ids.month || !ids.year) return null;
    if (!user || !user.active_pay || user.pending_cancel) return null;
    if (user.noteslist_integration_access && user.noteslist_integration_source === 'dual') return null;
    const w = studentWindow(user, now, ids);
    if (w.state !== 'current') return null;
    return {
        planName: w.interval === 'year' ? 'Cuelist Student Yearly' : 'Cuelist Student Monthly',
        endsLine: `Student pricing ends on ${formatDate(w.endsAt)}.`
    };
}

export function endedBanner(endsAt, years) {
    let span = 'four years';
    if (years !== DEFAULT_TERM_YEARS) span = years === 1 ? '1 year' : `${years} years`;
    return `Your ${span} of student pricing ended on ${formatDate(endsAt)}. Choose Monthly or Yearly instead.`;
}

export function returningBanner(endsAt) {
    return `Student pricing on this account ends on ${formatDate(endsAt)}.`;
}

// The confirm box for any switch that leaves account credit (Josh, 15 Oct 2026). `preview` is functions
// previewSwitch's reply: credit (cents), credit_display, next_charge {date, amount_display} or null.
export function creditLines(preview, { fromLabel, toStudent }) {
    if (!preview || !(preview.credit > 0) || preview.trialing || preview.deferred) return [];
    const lines = [`Unused time on ${fromLabel} becomes $${preview.credit_display} of account credit.`];
    const next = preview.next_charge;
    if (next && next.date && next.amount_display) {
        const when = formatDate(new Date(next.date));
        const what = toStudent ? 'your student price' : 'your plan';
        lines.push(`Your credit covers ${what} until ${when}. Your first charge after that is $${next.amount_display} on ${when}.`);
    }
    return lines;
}

export default { termYears, studentWindow, formatDate, endLine, endedBanner, returningBanner, creditLines };

// The Account page Subscription card while a cancel is set (Josh, 15 Oct 2026): every plan shows its name
// and the day it ends ("Your Cuelist Monthly subscription. Ends on 10/30/2026."). The date is the stored
// period end, which is the trial end for a trialing subscription. Not cancelling or not paying: null.

function planName(user, now, ids) {
    if (user.noteslist_integration_access && user.noteslist_integration_source === 'dual') return 'Cuelist + Notes List';
    const w = ids && ids.month && ids.year ? studentWindow(user, now, ids) : null;
    if ((w && w.state === 'current') || user.subscription_level === 'student') {
        const interval = w && w.state === 'current' ? w.interval : 'month';
        return interval === 'year' ? 'Cuelist Student Yearly' : 'Cuelist Student Monthly';
    }
    switch (user.subscription_level) {
        case 'monthly': return 'Cuelist Monthly';
        case 'yearly': return 'Cuelist Yearly';
        case 'educational': return 'Educational Plan';
        case 'institutional': return 'Institutional Plan';
        default: return 'Cuelist';
    }
}

export function cancellingLine(user, now, ids) {
    if (!user || !user.active_pay || !user.pending_cancel) return null;
    const ms = Date.parse(user.subscription_current_period_end || '');
    return {
        planName: planName(user, now || new Date(), ids),
        endsLine: Number.isFinite(ms) ? `Ends on ${formatDate(new Date(ms))}.` : 'Ends at the end of the current billing period.'
    };
}



// WEBPACK FOOTER //
// ./src/lib/student-plan.js